import { Navbar } from '../components/Navbar';
import { Footer } from '../components/Footer';
import { CTASection } from '../components/CTASection';
import { motion } from 'framer-motion';
import { Link } from 'react-router-dom';
import { Truck, Ship, Package, Forklift, Clock, Globe, ArrowRight, CheckCircle } from 'lucide-react';

const services = [
  {
    id: 'ltl-ftl',
    icon: Truck,
    title: 'LTL & FTL Freight',
    subtitle: 'Flexible Shipping for Any Volume',
    description: 'Whether you need to ship a few pallets or fill an entire trailer, we match your freight with the right capacity at competitive rates.',
    features: [
      'Less-than-truckload for smaller shipments',
      'Full truckload for maximum efficiency',
      'Partial loads for mid-size freight',
      'Dedicated capacity options available'
    ],
    image: 'https://images.unsplash.com/photo-1720127601642-7c3a7ba88f5f?crop=entropy&cs=srgb&fm=jpg&ixid=M3w4NjAzNzl8MHwxfHNlYXJjaHwzfHx3aWRlJTIwbG9hZCUyMHRydWNrJTIwaGlnaHdheSUyMHBlcm1pdCUyMG92ZXJzaXplZCUyMGNhcmdvfGVufDB8fHx8MTc2OTIxNDI1NHww&ixlib=rb-4.1.0&q=85&w=1200'
  },
  {
    id: 'equipment',
    icon: Package,
    title: 'Equipment Transport',
    subtitle: 'Heavy Machinery & Industrial Freight',
    description: 'Move construction equipment, manufacturing machinery, and industrial freight with carriers experienced in heavy haul.',
    features: [
      'Flatbed and step-deck trailers',
      'RGN (Removable Gooseneck) for heavy loads',
      'Experienced operators for loading/unloading',
      'Equipment securement and protection'
    ],
    image: 'https://images.pexels.com/photos/12230651/pexels-photo-12230651.jpeg?w=1200'
  },
  {
    id: 'oversized',
    icon: Forklift,
    title: 'Oversized & Specialized',
    subtitle: 'Beyond Standard Dimensions',
    description: 'Permits, pilot cars, route surveys—we handle the complexity of moving loads that exceed standard weight and dimension limits.',
    features: [
      'Permit acquisition and management',
      'Pilot car and escort coordination',
      'Route planning and surveys',
      'Specialized equipment matching'
    ],
    image: 'https://images.pexels.com/photos/27423205/pexels-photo-27423205.jpeg?w=1200'
  },
  {
    id: 'intermodal',
    icon: Ship,
    title: 'Port & Intermodal',
    subtitle: 'Seamless Container Movement',
    description: 'Coordinate the handoff between ocean, rail, and truck for efficient container drayage and intermodal solutions.',
    features: [
      'Port drayage services',
      'Rail to truck transload',
      'Container tracking and management',
      'Chassis provisioning'
    ],
    image: 'https://images.unsplash.com/photo-1765206257904-9c46302d88e5?crop=entropy&cs=srgb&fm=jpg&ixid=M3w3NTY2NzV8MHwxfHNlYXJjaHwzfHxjb250YWluZXIlMjBwb3J0JTIwc2hpcHBpbmclMjB5YXJkJTIwZGF5dGltZSUyMGNyYW5lc3xlbnwwfHx8fDE3NjkyMjA0MzR8MA&ixlib=rb-4.1.0&q=85&w=1200'
  },
  {
    id: 'expedited',
    icon: Clock,
    title: 'Expedited Freight',
    subtitle: 'When Time Is Critical',
    description: 'Hot shots, team drivers, and air freight options for time-sensitive shipments that can\'t wait.',
    features: [
      'Same-day and next-day delivery',
      'Team drivers for non-stop transit',
      'Real-time tracking and updates',
      'Dedicated capacity on demand'
    ],
    image: 'https://images.unsplash.com/photo-1583911026662-95161686d9a6?crop=entropy&cs=srgb&fm=jpg&ixid=M3w4NjAzMzl8MHwxfHNlYXJjaHwxfHxjYXJnbyUyMHBsYW5lJTIwbG9hZGluZyUyMGZyZWlnaHQlMjBhaXIlMjBjYXJnb3xlbnwwfHx8fDE3NjkxOTg1MTR8MA&ixlib=rb-4.1.0&q=85&w=1200'
  },
  {
    id: 'coverage',
    icon: Globe,
    title: 'Nationwide Coverage',
    subtitle: 'Coast to Coast Network',
    description: 'Our vetted carrier network spans all 48 contiguous states, delivering consistent service wherever you ship.',
    features: [
      '48-state coverage',
      'Cross-country lane expertise',
      'Regional and local delivery',
      'Consistent service standards'
    ],
    image: 'https://static.prod-images.emergentagent.com/jobs/2decc47a-ac14-4283-b5d1-97b4034d1d29/images/2c7f11d9d958e89bb2ef91fd4135e8152ddaba86c9e36737f91bfb389620561f.png'
  }
];

export default function ServicesPage() {
  return (
    <div className="min-h-screen bg-white" data-testid="services-page">
      <Navbar />
      
      {/* Hero Section */}
      <section className="pt-32 pb-16 bg-navy-900 relative overflow-hidden">
        <div className="absolute inset-0 opacity-10">
          <div className="absolute inset-0" style={{
            backgroundImage: `url("data:image/svg+xml,%3Csvg width='60' height='60' viewBox='0 0 60 60' xmlns='http://www.w3.org/2000/svg'%3E%3Cg fill='none' fill-rule='evenodd'%3E%3Cg fill='%23ffffff' fill-opacity='1'%3E%3Cpath d='M36 34v-4h-2v4h-4v2h4v4h2v-4h4v-2h-4zm0-30V0h-2v4h-4v2h4v4h2V6h4V4h-4zM6 34v-4H4v4H0v2h4v4h2v-4h4v-2H6zM6 4V0H4v4H0v2h4v4h2V6h4V4H6z'/%3E%3C/g%3E%3C/g%3E%3C/svg%3E")`,
          }} />
        </div>
        
        <div className="relative max-w-7xl mx-auto px-4 sm:px-6 lg:px-8 text-center">
          <motion.span
            initial={{ opacity: 0, y: 20 }}
            animate={{ opacity: 1, y: 0 }}
            className="inline-block px-4 py-2 bg-orange-500/10 border border-orange-500/30 rounded-full text-orange-500 font-body text-sm font-medium mb-4"
          >
            Our Services
          </motion.span>
          <motion.h1
            initial={{ opacity: 0, y: 20 }}
            animate={{ opacity: 1, y: 0 }}
            transition={{ delay: 0.1 }}
            className="font-heading text-4xl sm:text-5xl lg:text-6xl font-bold text-white uppercase"
          >
            Freight <span className="text-orange-500">Solutions</span>
          </motion.h1>
          <motion.p
            initial={{ opacity: 0, y: 20 }}
            animate={{ opacity: 1, y: 0 }}
            transition={{ delay: 0.2 }}
            className="mt-4 text-base lg:text-lg text-slate-400 font-body max-w-2xl mx-auto"
          >
            From standard LTL to complex oversized loads, we have the expertise and carrier network to move your freight.
          </motion.p>
        </div>
      </section>

      {/* Services List */}
      <section className="py-16 lg:py-24">
        <div className="max-w-7xl mx-auto px-4 sm:px-6 lg:px-8">
          <div className="space-y-24">
            {services.map((service, index) => (
              <motion.div
                key={service.id}
                id={service.id}
                initial={{ opacity: 0, y: 40 }}
                whileInView={{ opacity: 1, y: 0 }}
                viewport={{ once: true }}
                transition={{ duration: 0.6 }}
                data-testid={`service-detail-${service.id}`}
                className={`grid lg:grid-cols-2 gap-12 items-center ${
                  index % 2 === 1 ? 'lg:grid-flow-dense' : ''
                }`}
              >
                {/* Content */}
                <div className={index % 2 === 1 ? 'lg:col-start-2' : ''}>
                  <div className="w-14 h-14 bg-orange-500/10 rounded-xl flex items-center justify-center mb-6">
                    <service.icon className="w-7 h-7 text-orange-500" />
                  </div>
                  <h2 className="font-heading text-3xl sm:text-4xl font-bold text-navy-900 uppercase mb-2">
                    {service.title}
                  </h2>
                  <p className="font-body text-orange-600 font-medium mb-4">
                    {service.subtitle}
                  </p>
                  <p className="font-body text-slate-600 leading-relaxed mb-8">
                    {service.description}
                  </p>
                  <ul className="space-y-3 mb-8">
                    {service.features.map((feature) => (
                      <li key={feature} className="flex items-start gap-3">
                        <CheckCircle className="w-5 h-5 text-orange-500 mt-0.5 flex-shrink-0" />
                        <span className="font-body text-slate-700">{feature}</span>
                      </li>
                    ))}
                  </ul>
                  <Link
                    to="/quote"
                    className="inline-flex items-center gap-2 bg-orange-500 hover:bg-orange-600 text-white font-body font-semibold px-6 py-3 rounded-lg transition-colors duration-200 group"
                  >
                    Get a Quote
                    <ArrowRight className="w-4 h-4 group-hover:translate-x-1 transition-transform duration-200" />
                  </Link>
                </div>

                {/* Image */}
                <div className={index % 2 === 1 ? 'lg:col-start-1' : ''}>
                  <div className="rounded-2xl overflow-hidden shadow-xl">
                    <img
                      src={service.image}
                      alt={service.title}
                      className="w-full h-80 object-cover"
                    />
                  </div>
                </div>
              </motion.div>
            ))}
          </div>
        </div>
      </section>

      <CTASection />
      <Footer />
    </div>
  );
}
